'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Flight {
  id: string;
  airline: string;
  flightNumber: string;
  departure: string;
  checkInOpens: string;
  checkInUrl: string;
}

interface CheckInReminderProps {
  flights: Flight[];
}

export default function CheckInReminder({ flights }: CheckInReminderProps) {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-brand p-6 border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-surface rounded w-1/3" />
          <div className="h-20 bg-surface rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-brand p-6 border border-border">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-sky-blue/10 rounded-lg flex items-center justify-center">
          <Icon name="ClockIcon" size={20} className="text-sky-blue" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-lg text-foreground">
            Check-In Reminders
          </h3>
          <p className="text-sm text-muted-foreground">
            Automated notifications enabled
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {flights.map((flight) => (
          <div
            key={flight.id}
            className="p-4 bg-gradient-to-r from-sky-blue/5 to-transparent border border-sky-blue/20 rounded-lg"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-heading font-semibold text-foreground">
                  {flight.airline} {flight.flightNumber}
                </p>
                <p className="text-sm text-muted-foreground">Departure: {flight.departure}</p>
              </div>
              <span className="px-3 py-1 bg-sky-blue/10 text-sky-blue text-xs font-semibold rounded-full">
                Opens {flight.checkInOpens}
              </span>
            </div>

            <button className="w-full px-4 py-2.5 bg-sky-blue text-white font-heading font-semibold rounded-lg hover:bg-[#3A7BC8] transition-colors duration-300 flex items-center justify-center space-x-2">
              <Icon name="DevicePhoneMobileIcon" size={18} />
              <span>Get Mobile Boarding Pass</span>
            </button>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-surface rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="BellIcon" size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-foreground mb-1">Smart Notifications</p>
            <p className="text-sm text-muted-foreground">
              We&apos;ll send you reminders 24 hours before check-in opens and when your boarding pass is ready
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}